// services/captcha/broker.ts
//
// Hands a captcha challenge from the API layer (outside React) to the mounted
// <CaptchaHost/> and gives the token back as a promise, so the request that
// hit the captcha simply waits for it instead of failing and being replayed
// somewhere else. Keep this file React-free: services/api.ts imports it.

import { CaptchaChallenge, CaptchaError } from './types';

export type CaptchaSolver = (challenge: CaptchaChallenge) => Promise<string>;

let solver: CaptchaSolver | null = null;

// Tokens are single use, so two requests that both hit the captcha each need
// their own solve; they take turns rather than stacking widgets.
let queue: Promise<unknown> = Promise.resolve();

/** Registers the host that shows the widget; returns its unregister. */
export const registerCaptchaSolver = (fn: CaptchaSolver): (() => void) => {
  solver = fn;
  return () => {
    if (solver === fn) solver = null;
  };
};

export const hasCaptchaSolver = (): boolean => solver !== null;

/** Resolves with a fresh token, or rejects with a CaptchaError. */
export const requestCaptchaToken = (
  challenge: CaptchaChallenge
): Promise<string> => {
  const run = queue.then(() =>
    solver ? solver(challenge) : Promise.reject(new CaptchaError('no-host'))
  );
  queue = run.catch(() => undefined);
  return run;
};
